import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubApi } from "../../club-api-client";
import type { BulkAddMembers } from "../../types";
import { queryKeys } from "../query-keys";

export function useTrainingGroupMembers(clubId: string | null, groupId: string | null) {
	return useQuery({
		queryKey: queryKeys.clubPortal.trainingGroupMembers(clubId ?? "", groupId ?? ""),
		queryFn: () => clubApi.training.members.list(clubId!, groupId!),
		enabled: !!clubId && !!groupId,
	});
}

export function useAddTrainingGroupMember(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
			clubApi.training.members.add(clubId, groupId, userId),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training"],
			}),
	});
}

export function useRemoveTrainingGroupMember(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
			clubApi.training.members.remove(clubId, groupId, userId),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training"],
			}),
	});
}

export function useGroupMemberSuggestions(
	clubId: string | null,
	groupId: string | null,
	query?: string,
) {
	return useQuery({
		queryKey: queryKeys.clubPortal.trainingGroupSuggestions(clubId ?? "", groupId ?? "", query),
		queryFn: () => clubApi.training.members.suggestions(clubId!, groupId!, query),
		enabled: !!clubId && !!groupId,
	});
}

export function useBulkAddTrainingGroupMembers(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ groupId, data }: { groupId: string; data: BulkAddMembers }) =>
			clubApi.training.members.bulkAdd(clubId, groupId, data),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training"],
			}),
	});
}

export function usePromoteTrainingGroupMember(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
			clubApi.training.members.promote(clubId, groupId, userId),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training"],
			}),
	});
}
